import publicUrls from "../../routes/publicUrls";
import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";

import GalleryScope from "../../theme/GalleryScope";
import { PageContainer } from "../../components/gallery/Section";
import { tokens } from "../../theme/galleryTheme";



const { footerText, footerMuted } = tokens.color;
const mapSearchQuery = encodeURIComponent(
  "Leon Gallery G/F Eurovilla 1 V.A. Rufino corner Legazpi Street Makati"
);

const mapSrc = `https://maps.google.com/maps?q=${mapSearchQuery}&t=&z=17&ie=UTF8&iwloc=A&output=embed`;
const SOCIAL = [
  { label: "León Gallery on Facebook", href: "https://www.facebook.com/LeonGalleryMakati/", Icon: FacebookIcon },
  { label: "León Gallery on Instagram", href: "https://www.instagram.com/leongallerymakati", Icon: InstagramIcon },
  { label: "León Gallery on X (Twitter)", href: "https://twitter.com/leongalleryph", Icon: TwitterIcon },
  { label: "León Gallery on YouTube", href: "https://www.youtube.com/channel/UCcgsLbrD84mc26EuWzVptzA", Icon: YouTubeIcon },
];

// Existing routes only.
const EXPLORE = [
  { label: "Auctions", href: "/auctions" },
  { label: "Exhibitions", href: publicUrls.exhibitions() },
  { label: "Private sales", href: publicUrls.privateSales() },
  { label: "Stories", href: "/stories" },
  { label: "Our galleries", href: publicUrls.location("main") },
];
const SERVICES = [
  { label: "How to buy", href: "/how-to-buy" },
  { label: "How to sell", href: "/how-to-sell" },
  { label: "Request an estimate", href: "/request" },
  { label: "FAQ", href: "/faq" },
  { label: "About us", href: "/about" },
  { label: "Contact us", href: publicUrls.contact() },
];

// Column headings use the logo-tagline style: widely spaced serif capitals.
const headingSx = {
  fontFamily: tokens.font.display,
  fontSize: "0.875rem",
  fontWeight: 400,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: "#FFFFFF",
  mb: 2.5,
};

const linkSx = {
  color: footerText,
  fontSize: "0.9375rem",
  textDecoration: "none",
  display: "inline-flex",
  alignItems: "center",
  minHeight: 36,
  "&:hover": { color: "#FFFFFF", textDecoration: "underline", textUnderlineOffset: "4px" },
};

function LinkColumn({ title, links, id }) {
  return (
    <Box component="nav" aria-labelledby={id}>
      <Typography id={id} component="h2" sx={headingSx}>
        {title}
      </Typography>
      <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: 0.5 }}>
        {links.map((l) => (
          <li key={l.href}>
            <Box component="a" href={l.href} sx={linkSx}>
              {l.label}
            </Box>
          </li>
        ))}
      </Box>
    </Box>
  );
}

const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <GalleryScope>
      <Box
        component="footer"
        className="lg-gallery lg-gallery--dark"
        sx={{ bgcolor: tokens.color.footerBg, color: footerText, pt: { xs: 8, md: 10 }, pb: 4, mt: 10 }}
      >
        <PageContainer>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "minmax(0, 5fr) minmax(0, 2fr) minmax(0, 2fr) minmax(0, 3fr)" },
              gap: { xs: 6, md: 6 },
            }}
          >
            
            <Box sx={{ gridColumn: { sm: "1 / -1", md: "auto" }, display: "grid", gap: 3, alignContent: "start" }}>
              <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 400, fontSize: "2rem", color: "#FFFFFF", lineHeight: 1.15 }}>
                León Gallery
              </Typography>
              <Typography sx={{ color: footerText, fontSize: "0.9375rem", lineHeight: 1.75, maxWidth: "56ch" }}>
                The Philippines’ most trusted auction house. Specialists in culturally important &amp; museum-quality
                Philippine art and purveyors of fine Philippine antiques and historical objects. We sell a myriad of
                unique items including Old Master Drawings and Paintings, Modern and Contemporary Art, Furniture,
                Silver, Rare Books, Maps and Documents, Jewelry, Watches, Rugs and Carpets, Sculptures, Photographs
                and Ephemera.
              </Typography>
              <Box sx={{ display: "flex", gap: 1, ml: -1.25 }}>
                {SOCIAL.map(({ label, href, Icon }) => (
                  <IconButton
                    key={href}
                    component="a"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    sx={{ color: "#FFFFFF", width: 44, height: 44, "&:hover": { color: footerMuted } }}
                  >
                    <Icon fontSize="small" />
                  </IconButton>
                ))}
              </Box>
            </Box>

            <LinkColumn id="footer-explore" title="Explore" links={EXPLORE} />
            <LinkColumn id="footer-services" title="Services" links={SERVICES} />

            
            <Box component="section" aria-labelledby="footer-contact">
              <Typography id="footer-contact" component="h2" sx={headingSx}>
                Client services
              </Typography>
              <Box component="address" sx={{ fontStyle: "normal", display: "grid", gap: 2.5 }}>
                <Box>
                  <Typography sx={{ color: "#FFFFFF", fontWeight: 600, fontSize: "0.9375rem" }}>Eurovilla 1</Typography>
                  <Typography sx={{ color: footerText, fontSize: "0.9375rem", lineHeight: 1.7 }}>
                    <Box component="a" href="tel:+63288562781" sx={{ ...linkSx, minHeight: 0 }}>
                      (02) 8856 2781
                    </Box>
                    <br />
                    Makati City, Philippines
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ color: "#FFFFFF", fontWeight: 600, fontSize: "0.9375rem" }}>Enquiries</Typography>
                  <Box component="a" href="mailto:info@leon-gallery.com" sx={{ ...linkSx, minHeight: 0 }}>
                    info@leon-gallery.com
                  </Box>
                  <Typography sx={{ color: footerText, fontSize: "0.9375rem" }}>www.leon-gallery.com</Typography>
                </Box>
                <Box>
                  <Typography sx={{ color: "#FFFFFF", fontWeight: 600, fontSize: "0.9375rem" }}>Office hours</Typography>
                  <Typography sx={{ color: footerText, fontSize: "0.9375rem", lineHeight: 1.7 }}>
                    Monday – Saturday: 9:00 am – 6:00 pm
                    <br />
                    Sundays: Closed
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          
          <Box sx={{ mt: { xs: 7, md: 9 }, height: { xs: 260, md: 340 }, width: "100%", overflow: "hidden", border: "1px solid #2A2A2A" }}>
            <iframe
              title="Map: León Gallery, Eurovilla 1, Makati"
              src={mapSrc}
              width="100%"
              height="100%"
              style={{ border: 0, display: "block" }}
              allowFullScreen
              loading="lazy"
            />
          </Box>

          
          <Box
            sx={{
              mt: 5,
              pt: 3,
              borderTop: "1px solid #2A2A2A",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
            }}
          >
            <Typography sx={{ fontSize: "0.875rem", color: footerMuted, letterSpacing: "0.04em" }}>
              © {year} León Gallery. All rights reserved.
            </Typography>
            <Box component="nav" aria-label="Legal" sx={{ display: "flex", gap: 3 }}>
              <Box component="a" href="/privacy" sx={{ ...linkSx, fontSize: "0.875rem", color: footerMuted }}>
                Privacy policy
              </Box>
              <Box component="a" href="/terms-and-conditions" sx={{ ...linkSx, fontSize: "0.875rem", color: footerMuted }}>
                Terms &amp; conditions
              </Box>
            </Box>
          </Box>
        </PageContainer>
      </Box>
    </GalleryScope>
  );
};

export default Footer;
